import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useAccountStore } from '../../components/Stores/accountStore';
import { useNavigate, useParams } from 'react-router-dom';
import { useFileBatches } from '../../hooks/useFileBatches';
import { useUser } from '../../hooks/useUser';
import { LoadingSpinnerOverlay } from '../../components/UI-Elements/LoadingSpinnerOverlay';
import { IFileBatch } from '../../sp-types';
import { Timestamp } from 'firebase/firestore';
import ButtonDesigned from '../../components/UI-Elements/Buttons/ButtonDesigned';
import { allDocTypes, deleteFile, mapDocType, SideMenu, updateBatchFiles } from './upload-files-utils';
import { FilesPanel } from './components/FilesPanels';
import { OpenInNew } from '@mui/icons-material';
import { getAccountDocTypes, TextDesc } from '../../sp-firebase/docTypesFB';
import { ConfirmDelete } from '@components/UI-Elements/FileUpload/ErrorComponents/ConfirmDelete';

export const makeBatch = (accountId: string, userId: string, id?: string): IFileBatch => ({
  accountId,
  userId,
  id: id || uuidv4().slice(0, 6),
  createdAt: Timestamp.now(),
  files: {},
  status: 'draft',
  comments: [],
});

export const UploadManageFilesWrapper: React.FC = () => {
  const navigate = useNavigate();
  const { batchId } = useParams();
  const { account } = useAccountStore();
  const { data: userData } = useUser();
  const accountId = account?.id || '';
  const userId = userData?.id || '';
  return <UploadManageFiles accountId={accountId} userId={userId} batchId={batchId || ''} navigate={navigate} />;
};
export const UploadManageFiles: React.FC<{
  accountId: string;
  userId: string;
  batchId: string;
  navigate: any;
  status?: string;
}> = ({ accountId, userId, batchId, navigate, status }) => {
  const [loading, setLoading] = React.useState<boolean>(false);
  const [docTypes, setDocTypes] = React.useState<TextDesc[]>([]);

  const [emptyBatch, setEmptyBatch] = React.useState<IFileBatch | undefined>();

  const [confirmSubmitOpen, setConfirmSubmitOpen] = React.useState<boolean>(false);

  const { batches, isLoading, error, saveBatch, refetch: refetchBatches } = useFileBatches(accountId);

  React.useEffect(() => {
    const loadDocTypes = async (_accountId: string) => {
      setLoading(true);
      const docs = await getAccountDocTypes(_accountId);

      setDocTypes(docs?.length ? docs : allDocTypes);
      setLoading(false);
    };

    if (accountId && !batches?.find((a) => a.status === 'draft')) {
      setEmptyBatch(makeBatch(accountId, userId));
    }

    if (accountId) {
      loadDocTypes(accountId);
    }
  }, [accountId]);

  const [currBatch, setCurrBatch] = React.useState<Partial<IFileBatch>>(
    batches?.find((b) => b.id === batchId) || batches?.[0] || emptyBatch || {},
  );

  React.useEffect(() => {
    if (status) {
      refetchBatches();
    }
  }, [status]);

  React.useEffect(() => {
    const batch = batches?.find((b) => b.id === batchId) || batches?.[0] || emptyBatch || {};
    setCurrBatch(batch);
  }, [batchId, batches, emptyBatch, status]);

  const [docType, setDocType] = React.useState<string>('general');

  const handleBatchClick = React.useCallback((id: string) => navigate(`/upload-files/${id}`), [navigate]);

  const onFilesChange = async (filePaths: string[], fileUrls: string[], aDocType: string) => {
    setCurrBatch((prevBatch) => {
      const updatedBatch = updateBatchFiles(prevBatch as IFileBatch, filePaths, fileUrls, aDocType);
      saveBatch(updatedBatch).then(() => refetchBatches());
      return updatedBatch;
    });
  };

  const onFileDelete = async (fileIndex: number, aDocType: string) => {
    setCurrBatch((prevBatch) => {
      const updatedBatch = deleteFile(prevBatch as IFileBatch, fileIndex, aDocType);
      saveBatch(updatedBatch).then(() => refetchBatches());
      return updatedBatch;
    });
  };

  const createNewBatch = async () => {
    setLoading(true);
    await saveBatch(makeBatch(accountId, userId));

    refetchBatches();
    setLoading(false);
  };

  const submitConfirm = async () => {
    setConfirmSubmitOpen(false);

    setLoading(true);
    const updatedBat = { ...currBatch, status: 'pending' };
    await saveBatch(updatedBat);
    setCurrBatch(updatedBat);
    refetchBatches();
    setLoading(false);
  };

  const onSubmitBatch = async () => {
    setConfirmSubmitOpen(true);
  };

  if (isLoading) return <div>Loading batches...</div>;
  if (error) return <div>Error loading batches.</div>;

  return (
    <div className="flex flex-col w-full h-full">
      {(loading || isLoading) && <LoadingSpinnerOverlay />}
      <div className="flex flex-1 gap-3">
        <SideMenu
          batches={batches || [emptyBatch || {}]}
          onSelectBatch={handleBatchClick}
          selectedBatch={batchId || (batches || [emptyBatch || {}])[0].id}
          docTypes={docTypes.map(mapDocType)}
          onSelectDocType={(type) => setDocType(type)}
          selectedType={docType}
          onCreateBatch={createNewBatch}
        />

        <main className="flex-1 flex flex-col p-4 overflow-auto bg-white  shadow-custom">
          <h4>Upload & Manage files</h4>
          <p className="text-sm text-gray-400 ">Upload and submit your files </p>
          <div className="flex h-full gap-3 justify-center items-center">
            <FilesPanel
              batch={currBatch}
              {...{ docType, setLoading, onFilesChange, onFileDelete, accountId, userId }}
            />
          </div>

          <div className="flex p-3 justify-between">
            {/* <ButtonDesigned style="outline" onButtonClick={console.log} className="px-4">
              Cancel
            </ButtonDesigned> */}
            <div>
              {currBatch.resultFile && (
                <ButtonDesigned
                  style="primary"
                  onButtonClick={() => window.open(currBatch.resultFile?.url)}
                  className="px-4"
                >
                  Download results file
                  <OpenInNew />
                </ButtonDesigned>
              )}
            </div>
            <ButtonDesigned
              style="primary"
              onButtonClick={onSubmitBatch}
              className="px-4"
              disableButton={['pending', 'done'].includes(currBatch.status || '')}
            >
              Submit
            </ButtonDesigned>
          </div>
        </main>
      </div>
      {/* {//stateVariable  
      //  false && <ConfirmDelete 
      title="Sure you want to submit" 
      description="Are ypou sue./.." />}  */}
      {confirmSubmitOpen && (
        <ConfirmDelete
          onAction={submitConfirm}
          title={'confirm'}
          actionText={'Confirm'}
          description={'After approval, you can upload another file by clicking the Create new batch button.'}
          onCancel={() => setConfirmSubmitOpen(false)}
        />
      )}
    </div>
  );
};
